import {$s, store} from '@bitstillery/common/app'
import i18next from 'i18next'
import m from '@bitstillery/mithril'
import {api, cms_content_path, logger, notifier} from '@bitstillery/common/app'
import {PlaceholderPackage, SupportedLanguage} from '@bitstillery/common/api/fact2server'
import {current_i18n_package} from '@bitstillery/common/lib/i18n_packages'
import {text_before} from '@bitstillery/common/lib/utils'

/**
 * Endonyms: a language is always listed under its own name, never translated into the active
 * UI language — a Japanese visitor looking for Dutch scans for "Nederlands", not "オランダ語".
 */
export const language_names: Record<SupportedLanguage, string> = {
    [SupportedLanguage.AR]: 'العربية',
    [SupportedLanguage.DE]: 'Deutsch',
    [SupportedLanguage.EN_GB]: 'English',
    [SupportedLanguage.ES]: 'Español',
    [SupportedLanguage.FR]: 'Français',
    [SupportedLanguage.IT]: 'Italiano',
    [SupportedLanguage.JA]: '日本語',
    [SupportedLanguage.NL]: 'Nederlands',
    [SupportedLanguage.ZH]: '中文',
}

const language_pairs = (codes: SupportedLanguage[]): [string, string][] => codes.map((code) => [code, language_names[code]])

export const supported_languages =
    process.env.MSI_PACKAGE === 'discover'
        ? language_pairs([SupportedLanguage.EN_GB])
        : language_pairs([
              SupportedLanguage.EN_GB,
              SupportedLanguage.DE,
              SupportedLanguage.FR,
              SupportedLanguage.IT,
              SupportedLanguage.ES,
              SupportedLanguage.NL,
              SupportedLanguage.ZH,
              SupportedLanguage.JA,
              SupportedLanguage.AR,
          ])

export const supported_portal_languages = language_pairs([
    SupportedLanguage.NL,
    SupportedLanguage.EN_GB,
    SupportedLanguage.FR,
    SupportedLanguage.DE,
    SupportedLanguage.IT,
    SupportedLanguage.ES,
    SupportedLanguage.ZH,
    SupportedLanguage.JA,
    SupportedLanguage.AR,
])

export async function set_language(language_code: string): Promise<void> {
    if (!supported_languages.map((option) => option[0]).includes(language_code)) {
        return
    }
    logger.info(`set language: ${language_code}`)
    $s.language = language_code

    const i18n_package = current_i18n_package()
    // Discover's user has no portal profile to store the language on; the portal's profile always
    // has a price preference, which the update requires.
    const price_preference = $s.identity.user.price_preference
    if ($s.identity.token && i18n_package !== PlaceholderPackage.Discover && price_preference) {
        await api.post(
            'portal/user/profile',
            {
                email_address: $s.identity.user.email_address ?? '',
                first_name: $s.identity.user.first_name ?? '',
                language: language_code,
                last_name: $s.identity.user.last_name ?? null,
                mobile_phone_number: $s.identity.user.mobile_phone_number ?? null,
                phone_number: $s.identity.user.phone_number ?? null,
                price_preference,
                timezone:
                    $s.identity.user.timezone ??
                    (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'Europe/Amsterdam'),
            },
            true,
        )
    }

    void store.save()

    const params: {language_code: string; package: PlaceholderPackage; path?: string} = {language_code, package: i18n_package}
    if (i18n_package !== PlaceholderPackage.Discover) {
        // Overlay routes (/login, …) render the '/' home page behind their dialog; map to the content
        // path so a language switch during login fetches the home page's placeholders, not '/login'.
        params.path = cms_content_path($s.env.uri)
    }
    const {result, success} = await api.get(`portal/cms/i18n`, params)
    if (!success) {
        notifier.notify('Cannot load the translations', 'warning')
        return
    }
    // A language without a single placeholder translation is absent from the response.
    const translation = result.resources[language_code]?.translation
    if (translation) {
        // deep + overwrite so page keys merge onto the globals loaded at init instead of replacing them.
        i18next.addResourceBundle(language_code, 'translation', translation, true, true)
    }
    await i18next.changeLanguage(language_code)
    m.redraw()
}

/**
 * Merge one CMS page's placeholders (those for `path`, plus globals) into the live i18next bundle
 * without dropping already-loaded keys. The portal loads placeholders scoped to the current URL at
 * boot; sandbox fixtures live under `/sandbox/...`, so they call this in `oninit` with their official
 * portal URI (e.g. `load_placeholders('/about-us')`) to resolve page-bound `$t` keys.
 */
export async function load_placeholders(path: string): Promise<void> {
    const language_code = $s.language
    const i18n_package = current_i18n_package()
    const {result, success} = await api.get(`portal/cms/i18n`, {language_code, package: i18n_package, path})
    if (!success) {
        logger.warn(`cannot load placeholders for path: ${path}`)
        return
    }
    const translation = result.resources[language_code]?.translation
    if (translation) {
        // deep + overwrite so page keys merge onto the globals loaded at init instead of replacing them.
        i18next.addResourceBundle(language_code, 'translation', translation, true, true)
        m.redraw()
    }
}

/**
 * Count all leaf nodes (non-object values) in an i18n translation object
 */
export function count_i18n_tags(obj: unknown): number {
    let count = 0
    const traverse = (current: unknown) => {
        if (typeof current === 'object' && current !== null && !Array.isArray(current)) {
            for (const value of Object.values(current)) {
                traverse(value)
            }
        } else {
            count++
        }
    }
    traverse(obj)
    return count
}

/**
 * Some storefronts name their language in the host's leading label rather than its TLD —
 * `arab.movingspiritsinternational.com` is the Arabic front for a .com domain. Matching the label
 * (not the full host) keeps staging and dev variants of the same storefront working.
 */
const subdomain_languages: Partial<Record<string, SupportedLanguage>> = {
    arab: SupportedLanguage.AR,
}

/**
 * The country domain a visitor typed is a deliberate choice of storefront, so it decides the
 * language when nothing more specific (a `?language=` link, a profile, an earlier switch) does.
 * Only ccTLDs whose language the portal actually speaks are listed; everything else (.com, .eu,
 * a dev host) resolves to nothing and leaves the decision to the next check.
 */
const tld_languages: Partial<Record<string, SupportedLanguage>> = {
    at: SupportedLanguage.DE,
    be: SupportedLanguage.NL,
    cn: SupportedLanguage.ZH,
    de: SupportedLanguage.DE,
    es: SupportedLanguage.ES,
    fr: SupportedLanguage.FR,
    it: SupportedLanguage.IT,
    jp: SupportedLanguage.JA,
    nl: SupportedLanguage.NL,
    uk: SupportedLanguage.EN_GB,
}

export function domain_language(hostname: string): string | null {
    const labels = hostname.toLowerCase().split('.')
    // The subdomain is the more specific signal: an Arabic storefront on a .nl domain is still Arabic.
    const subdomain = labels.length > 1 ? labels[0] : undefined
    const tld = labels.at(-1)
    return (subdomain && subdomain_languages[subdomain]) || (tld && tld_languages[tld]) || null
}

/**
 * Browsers report a region tag (`nl-NL`, `de-AT`); we author copy per language and use a single
 * English flavo(u)r, so match the exact tag first and then its base tag.
 */
export function normalize_language(code: string, supported: Set<string>): string | null {
    if (supported.has(code)) {
        return code
    }
    const base = text_before(code, '-').toLowerCase()
    if (base === 'en') {
        return supported.has(SupportedLanguage.EN_GB) ? SupportedLanguage.EN_GB : null
    }
    return supported.has(base) ? base : null
}
